import { LiveKitRoom } from "@livekit/components-react"
import "@livekit/components-styles"
import { Stack } from "@mui/material"
import * as Sentry from "@sentry/react"
import { MediaDeviceFailure, Participant } from "livekit-client"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import Background from "../../components/background"
import CallLoading from "../../components/CustomVideoConference/CallLoading"
import { Header } from "../../components/header"
import Overlay from "../../components/Modal/Overlay"
import { updateCallDuration } from "../../external/firestore"
import { AnalyticsEvents } from "../../types/Analytics"
import { CallDisconnectType } from "../../types/Call"
import { SubscriptionPlan } from "../../types/Subscription"
import {
  getAvailableCredits,
  isInIframe,
  parseJwt,
  postMessageToParent,
} from "../../utils/func"
import {
  deleteLivekitRoom,
  getDefaultAgentId,
  getDefaultCreatorId,
  getLivekitToken,
  isDev,
  isLocalDev,
  isStaging,
} from "../../utils/general"
import { useAppContext, useAppDispatchContext } from "../Context"
import { useMixpanel } from "../hooks/useMixpanel"
import { MyVideoConference } from "./MyVideoConference"

const MAX_CALL_DURATION = 3 * 60 // 3 minutes
const IDLE_TIMEOUT = 2 * 60 // 2 minutes
const BUFFER_TIME = 2 // 2 seconds

// When running multiple video generation models in parallel, we need to connect to different LiveKit servers
// However, let's leave this as a backup in case the token does not contain the server URL as metadata
const defaultServerUrl =
  import.meta.env.VITE_LIVEKIT_URL ??
  (isLocalDev
    ? "ws://localhost:7880"
    : isDev
      ? "wss://dev-38rwe5wn.livekit.cloud"
      : isStaging
        ? "wss://staging-56walglr.livekit.cloud"
        : "wss://prod-w0h88kyi.livekit.cloud")

export default function LiveRoom() {
  const context = useAppContext()
  const dispatch = useAppDispatchContext()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEmbed = isInIframe()

  const [serverUrl, setServerUrl] = useState(defaultServerUrl)
  const [token, setToken] = useState("")
  const [isConnected, setIsConnected] = useState(false)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [lastMessageTime, setLastMessageTime] = useState(Date.now())
  const [duration, setDuration] = useState(0)
  const [maxCallDuration, setMaxCallDuration] = useState(MAX_CALL_DURATION)
  const [disconnectReason, setDisconnectReason] = useState<
    CallDisconnectType.SessionExpired | CallDisconnectType.CreditsExhausted
  >(CallDisconnectType.SessionExpired)
  const [sessionStartTime] = useState(new Date(Date.now() + BUFFER_TIME * 1000))
  const [initialCallDuration, setInitialCallDuration] = useState(0)
  const [speakingParticipants, setSpeakingParticipants] = useState<string[]>([])
  const hasDisconnected = useRef(false)

  const { logAnalyticsEvent } = useMixpanel()

  const { agentUsage, agent } = context

  useEffect(() => {
    const resetCallDuration = async () => {
      if (id == null) return
      await updateCallDuration(id, { callDuration: 0 })
      setInitialCallDuration(0)
    }
    resetCallDuration()
  }, [id, navigate])

  const roomOptions = useMemo(
    () => ({
      audioCaptureDefaults: context.audioDevice
        ? {
            deviceId: context.audioDevice,
          }
        : undefined,
      videoCaptureDefaults: context.videoDevice
        ? {
            deviceId: context.videoDevice,
          }
        : undefined,
    }),
    [],
  )

  useEffect(() => {
    const req = async () => {
      const tokenFromBackend = await getLivekitToken(id)
      // read out metadata from JWT
      const jwtPayload = parseJwt(tokenFromBackend)
      const metadataString = jwtPayload.metadata || "{}"
      const metadata = JSON.parse(metadataString)
      const serverUrl = metadata["livekitUrl"] || defaultServerUrl
      setServerUrl(serverUrl)
      setToken(tokenFromBackend)
    }
    req()
  }, [id])

  const onDisconnected = useCallback(
    async (callDisconnectType: CallDisconnectType | null = null) => {
      if (hasDisconnected.current) {
        return
      }
      hasDisconnected.current = true

      try {
        const currentSessionDuration = Math.floor(
          (new Date().getTime() - sessionStartTime.getTime()) / 1000,
        )
        const finalDuration = currentSessionDuration + initialCallDuration

        logAnalyticsEvent(AnalyticsEvents.CallEnded, {
          duration: finalDuration,
          callDisconnectReason: callDisconnectType,
        })

        postMessageToParent({
          type: "CALL_ENDED",
          callId: id,
          duration: finalDuration,
          reason: callDisconnectType,
        })

        await updateCallDuration(id, {
          callDuration: finalDuration,
          endedAt: new Date(),
        }).then(() => {
          if (participants.every((p) => p.isLocal || p.isAgent)) {
            deleteLivekitRoom(id)
          }
          navigate("/rooms/" + id + "/post", { state: callDisconnectType })
        })
      } catch (error) {
        console.error("Error during disconnect:", error)
        navigate("/rooms/" + id + "/post", { state: callDisconnectType })
      }
    },
    [
      dispatch,
      id,
      initialCallDuration,
      navigate,
      participants,
      sessionStartTime,
    ],
  )

  useEffect(() => {
    if (agent?.custom_branding) {
      return
    }

    // timer to check if the user is idle
    const idleCheck = setInterval(() => {
      const idle = Date.now() - lastMessageTime
      // Only disconnect if there are participants but none are speaking
      if (
        idle >= IDLE_TIMEOUT * 1000 &&
        participants.length > 0 &&
        speakingParticipants.length === 0
      ) {
        logAnalyticsEvent(AnalyticsEvents.IdleTimeout)
        onDisconnected(CallDisconnectType.InActive)
      }
    }, 1000)

    return () => clearInterval(idleCheck)
  }, [
    lastMessageTime,
    onDisconnected,
    participants,
    speakingParticipants,
    agent?.custom_branding,
  ])

  useEffect(() => {
    // timer to check if the call should end
    if (maxCallDuration <= 0) return

    if (duration >= maxCallDuration) {
      onDisconnected(disconnectReason)
    }
  }, [duration, maxCallDuration, disconnectReason, onDisconnected])

  useEffect(() => {
    let timer: NodeJS.Timeout

    timer = setInterval(() => {
      const currentSessionDuration = Math.floor(
        (new Date().getTime() - sessionStartTime.getTime()) / 1000,
      )
      setDuration(currentSessionDuration + initialCallDuration)
    }, 1000)

    return () => {
      if (timer) clearInterval(timer)
    }
  }, [sessionStartTime, initialCallDuration])

  useEffect(() => {
    if (
      !agent ||
      !agentUsage ||
      agent.id === getDefaultAgentId ||
      agent.creatorId === getDefaultCreatorId
    ) {
      return
    }

    const agentMaxCallDuration = Math.floor(
      (agent.max_session_length_minutes ?? 3) * 60,
    )

    if (agentUsage.pay_as_you_go_enabled) {
      setMaxCallDuration(agentMaxCallDuration)
      setDisconnectReason(CallDisconnectType.SessionExpired)
      return
    }

    let remainingCredits = Math.floor(getAvailableCredits(agentUsage))

    if (
      agentUsage.subscription_plan === SubscriptionPlan.FREE &&
      remainingCredits > 0
    ) {
      remainingCredits = MAX_CALL_DURATION
    }

    if (agent.max_session_length_minutes === -1 && remainingCredits > 0) {
      setMaxCallDuration(Math.floor(remainingCredits))
      setDisconnectReason(CallDisconnectType.CreditsExhausted)
      return
    }

    const maxCallDuration = Math.floor(
      Math.min(agentMaxCallDuration, remainingCredits),
    )

    setMaxCallDuration(maxCallDuration)

    if (remainingCredits < agentMaxCallDuration) {
      setDisconnectReason(CallDisconnectType.CreditsExhausted)
    }

    if (maxCallDuration <= 0) {
      onDisconnected(CallDisconnectType.CreditsExhausted)
    }
  }, [agentUsage, agent])

  const handleUserMessage = useCallback(() => {
    setLastMessageTime(Date.now())
  }, [])

  const handleParticipantSpeaking = useCallback(
    (speakingParticipants: string[]) => {
      if (speakingParticipants.length > 0) {
        setLastMessageTime(Date.now())
      }
      setSpeakingParticipants(speakingParticipants)
    },
    [],
  )

  const handleMediaDeviceFailure = useCallback(
    (failure: MediaDeviceFailure) => {
      Sentry.captureMessage("Media Device Failure", {
        level: "error",
        tags: {
          "media.device.failure": failure.toString(),
        },
        extra: {
          mediaDeviceFailure: failure,
        },
      })
    },
    [],
  )

  const handleConnected = useCallback(() => {
    setIsConnected(true)
    const mediaStates = {
      audioEnabled: context.audioEnabled,
      videoEnabled: context.videoEnabled,
      connectionTime: new Date().toISOString(),
    }

    if (isDev && !isLocalDev) {
      console.log("LiveKit Room Connected - Media States:", mediaStates)

      Sentry.captureEvent({
        message: "LiveKit Room Media States",
        level: "info",
        tags: {
          "media.connection": "success",
          "media.audio.enabled": String(context.audioEnabled),
          "media.video.enabled": String(context.videoEnabled),
        },
        extra: {
          mediaStates,
        },
      })

      Sentry.addBreadcrumb({
        category: "media",
        message: "Media states after LiveKit room connection",
        level: "info",
        data: mediaStates,
      })

      Sentry.setContext("media_devices", mediaStates)
    }

    postMessageToParent({
      type: "CALL_STARTED",
      callId: id,
    })
  }, [context.audioEnabled, context.videoEnabled, id])

  const handleDisconnected = useCallback(() => {
    setIsConnected(false)
  }, [])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      if (isConnected) onDisconnected()
      return undefined
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [isConnected, onDisconnected])

  return (
    <Stack
      direction={"column"}
      position={"relative"}
      minHeight={"100vh"}
      width={"100%"}
      overflow={"hidden"}
    >
      {!isEmbed && <Header />}
      <LiveKitRoom
        token={token}
        serverUrl={serverUrl}
        data-lk-theme="default"
        audio={context.audioEnabled}
        video={context.videoEnabled}
        style={{ height: isEmbed ? "100dvh" : "90dvh" }}
        onDisconnected={() => onDisconnected()}
        onConnected={handleConnected}
        options={roomOptions}
        onMediaDeviceFailure={handleMediaDeviceFailure}
      >
        {!isConnected && <CallLoading />}
        {isConnected && (
          <MyVideoConference
            setParticipants={setParticipants}
            isEmbed={isEmbed}
            maxDuration={maxCallDuration}
            onUserMessage={handleUserMessage}
            onDisconnected={onDisconnected}
            onParticipantSpeaking={handleParticipantSpeaking}
            duration={duration}
          />
        )}
      </LiveKitRoom>
      <Overlay open={!isConnected} />
      <Background />
    </Stack>
  )
}
